/**
 * App store: configuration, auth, document lifecycle and cloud sync.
 * Plain TS + useSyncExternalStore; no state library.
 */

import { FrondConfig, loadConfig } from "@frond/config";
import { AuthTokens, DocumentMeta, FrondClient } from "@frond/client";
import { useSyncExternalStore } from "react";
import { idbGet, idbPut, idbDelete } from "./idb";

const TOKENS_KEY = "frond.tokens";
const PKCE_KEY = "frond.pkce";

interface Tokens extends AuthTokens {
  /** epoch ms when the id token expires */
  idExpiresAt: number;
  email: string | null;
}

export interface AppState {
  booted: boolean;
  config: FrondConfig | null;
  configError: boolean;
  client: FrondClient | null;
  auth: { status: "signed-out" } | { status: "signed-in"; email: string | null };
  doc: {
    id: string | null;
    name: string;
    localOnly: boolean;
    saveState: "clean" | "dirty" | "saving" | "saved" | "error";
    savedAt: number | null;
  };
}

type Listener = () => void;

class Store {
  private state: AppState = {
    booted: false,
    config: null,
    configError: false,
    client: null,
    auth: { status: "signed-out" },
    doc: {
      id: null,
      name: "Untitled canvas",
      localOnly: true,
      saveState: "clean",
      savedAt: null,
    },
  };
  private listeners = new Set<Listener>();
  private tokens: Tokens | null = null;
  private localSaveTimer: ReturnType<typeof setTimeout> | null = null;
  private cloudSaveTimer: ReturnType<typeof setTimeout> | null = null;

  /** Engine setter (wired by the canvas view); used for serialization. */
  engineSerializer: (() => string) | null = null;
  engineLoader: ((json: string, historyJson?: string | null) => void) | null = null;
  engineHistorySerializer: (() => string) | null = null;
  engineHistoryLoader: ((json: string) => void) | null = null;
  /** Called by the engine when the document changes. */
  onDocumentChanged: (() => void) | null = null;

  private emit(): void {
    for (const fn of this.listeners) fn();
  }

  subscribe = (fn: Listener): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = (): AppState => this.state;

  private set(patch: Partial<AppState>): void {
    this.state = { ...this.state, ...patch };
    this.emit();
  }

  async boot(): Promise<void> {
    try {
      const config = await loadConfig();
      const client = new FrondClient(config, () => this.getIdToken());
      this.tokens = readTokens();
      const auth: AppState["auth"] = this.tokens
        ? { status: "signed-in", email: this.tokens.email ?? null }
        : { status: "signed-out" };
      this.set({ booted: true, config, client, auth });
    } catch {
      // Config missing/unreachable: run fully local (no cloud sync).
      this.tokens = readTokens();
      const auth: AppState["auth"] = this.tokens
        ? { status: "signed-in", email: this.tokens.email ?? null }
        : { status: "signed-out" };
      this.set({ booted: true, configError: true, auth });
    }
  }

  /* -------------------------------- auth --------------------------------- */

  async signIn(): Promise<void> {
    const config = this.state.config;
    if (!config || !this.state.client) return;
    const { generateCodeVerifier, generateCodeChallenge } = await import("@frond/client");
    const verifier = generateCodeVerifier();
    const challenge = await generateCodeChallenge(verifier);
    sessionStorage.setItem(PKCE_KEY, JSON.stringify({ verifier, state: "frond" }));
    window.location.assign(this.state.client.authorizeUrl(challenge));
  }

  async signUp(): Promise<void> {
    const config = this.state.config;
    if (!config || !this.state.client) return;
    const { generateCodeVerifier, generateCodeChallenge } = await import("@frond/client");
    const verifier = generateCodeVerifier();
    const challenge = await generateCodeChallenge(verifier);
    sessionStorage.setItem(PKCE_KEY, JSON.stringify({ verifier, state: "frond" }));
    window.location.assign(this.state.client.signUpUrl(challenge));
  }

  /** Dev mock login for testing offline or when AWS Cognito isn't reachable. */
  signInDev(email = "dev-user@example.com"): void {
    const fakeIdToken = createDevJwt(email);
    this.tokens = {
      id_token: fakeIdToken,
      access_token: "dev-access-token",
      refresh_token: "dev-refresh-token",
      expires_in: 86400,
      token_type: "Bearer",
      idExpiresAt: Date.now() + 86400 * 1000,
      email,
    };
    localStorage.setItem(TOKENS_KEY, JSON.stringify(this.tokens));
    this.set({ auth: { status: "signed-in", email } });
    if (this.state.client) {
      void this.saveNow();
    }
  }

  async handleCallback(code: string): Promise<void> {
    const pkceRaw = sessionStorage.getItem(PKCE_KEY);
    if (!pkceRaw || !this.state.client) return;
    const { verifier } = JSON.parse(pkceRaw) as { verifier: string };
    sessionStorage.removeItem(PKCE_KEY);
    const tokens = await this.state.client.exchangeCode(code, verifier);
    this.storeTokens(tokens);
    // If the user was drawing before signing in/up, sync current work to cloud
    if (this.state.doc.saveState !== "clean" || this.state.doc.localOnly) {
      void this.saveCloud();
    }
  }

  signOut(): void {
    this.tokens = null;
    localStorage.removeItem(TOKENS_KEY);
    this.set({ auth: { status: "signed-out" } });
    this.state.client?.signOut();
  }

  private storeTokens(tokens: AuthTokens): void {
    const payload = decodeJwt(tokens.id_token);
    this.tokens = {
      ...tokens,
      idExpiresAt: Date.now() + tokens.expires_in * 1000,
      email: payload?.email ?? null,
    };
    localStorage.setItem(TOKENS_KEY, JSON.stringify(this.tokens));
    this.set({ auth: { status: "signed-in", email: this.tokens.email ?? null } });
  }

  async getIdToken(): Promise<string | null> {
    if (!this.tokens || !this.state.config) return null;
    if (Date.now() > this.tokens.idExpiresAt - 30_000) {
      try {
        const fresh = await this.state.client!.refreshTokens(this.tokens.refresh_token);
        this.storeTokens(fresh);
      } catch {
        this.signOut();
        return null;
      }
    }
    return this.tokens.id_token;
  }

  /* ------------------------------ documents ------------------------------ */

  markDirty(): void {
    const doc = this.state.doc;
    if (doc.saveState === "dirty") {
      this.emit();
      return;
    }
    this.set({ doc: { ...doc, saveState: "dirty" } });
    this.scheduleLocalSave();
    this.scheduleCloudSave();
  }

  /** Explicit manual save: triggers immediate local and cloud save. */
  async saveNow(): Promise<boolean> {
    if (this.localSaveTimer) {
      clearTimeout(this.localSaveTimer);
      this.localSaveTimer = null;
    }
    if (this.cloudSaveTimer) {
      clearTimeout(this.cloudSaveTimer);
      this.cloudSaveTimer = null;
    }

    if (!this.engineSerializer) return false;

    if (this.state.client && this.state.auth.status === "signed-in") {
      await this.saveCloud();
      return this.state.doc.saveState === "saved";
    }

    await this.saveLocal();
    return true;
  }

  private scheduleLocalSave(): void {
    if (this.localSaveTimer) clearTimeout(this.localSaveTimer);
    this.localSaveTimer = setTimeout(() => {
      this.localSaveTimer = null;
      void this.saveLocal();
    }, 1000);
  }

  /** Local-first: the document always persists in IndexedDB. */
  private async saveLocal(): Promise<void> {
    if (!this.engineSerializer) return;
    const key = localKey(this.state.doc.id);
    const now = Date.now();
    const body = this.engineSerializer();
    const history = readHistorySnapshot(this.engineHistorySerializer);
    try {
      await idbPut(key, { name: this.state.doc.name, body, history, at: now });
      // When offline or signed-out, updating local IDB marks the document saved!
      if (this.state.auth.status !== "signed-in") {
        this.set({
          doc: {
            ...this.state.doc,
            saveState: "saved",
            savedAt: now,
          },
        });
      }
    } catch {
      // Storage pressure (history can be large): fall back to saving the
      // drawing alone so the work itself is never lost because of undo data.
      try {
        await idbPut(key, { name: this.state.doc.name, body, at: now });
        if (this.state.auth.status !== "signed-in") {
          this.set({
            doc: {
              ...this.state.doc,
              saveState: "saved",
              savedAt: now,
            },
          });
        }
      } catch {
        if (this.state.auth.status !== "signed-in") {
          this.set({ doc: { ...this.state.doc, saveState: "error" } });
        }
      }
    }
  }

  async restoreLocal(id: string | null): Promise<boolean> {
    const key = localKey(id);
    const saved = (await idbGet(key)) as
      | { name?: string; body?: string; history?: string | object }
      | undefined;
    if (saved?.body && this.engineLoader) {
      const historyJson =
        typeof saved.history === "string"
          ? saved.history
          : saved.history
            ? JSON.stringify(saved.history)
            : null;
      try {
        this.engineLoader(saved.body, historyJson);
      } catch {
        return false;
      }
      // Backward compat: very old engineLoader implementations ignore the
      // second argument and clear history — restore via the dedicated
      // history loader when available.
      if (historyJson && this.engineHistoryLoader) {
        try {
          this.engineHistoryLoader(historyJson);
        } catch {
          /* corrupt history: keep the restored drawing, start fresh undo */
        }
      }
      if (saved.name) this.set({ doc: { ...this.state.doc, name: saved.name } });
      return true;
    }
    return false;
  }

  async deleteLocal(id: string | null): Promise<void> {
    await idbDelete(localKey(id));
  }

  async newDocument(): Promise<void> {
    if (this.localSaveTimer) clearTimeout(this.localSaveTimer);
    if (this.cloudSaveTimer) clearTimeout(this.cloudSaveTimer);
    this.localSaveTimer = null;
    this.cloudSaveTimer = null;
    await this.deleteLocal(null);
    this.set({
      doc: {
        id: null,
        name: "Untitled canvas",
        localOnly: true,
        saveState: "clean",
        savedAt: null,
      },
    });
  }

  private scheduleCloudSave(): void {
    if (!this.state.client || this.state.auth.status !== "signed-in") return;
    // Debounced cloud upload after the local save settles.
    if (this.cloudSaveTimer) clearTimeout(this.cloudSaveTimer);
    this.cloudSaveTimer = setTimeout(() => {
      this.cloudSaveTimer = null;
      void this.saveCloud();
    }, 3000);
  }

  private async saveCloud(): Promise<void> {
    const { client, doc } = this.state;
    if (!client || !this.engineSerializer) return;
    this.set({ doc: { ...doc, saveState: "saving" } });
    try {
      let id = doc.id;
      let revision = 0;
      if (!id) {
        const ref = await client.createDocument(doc.name || "Untitled canvas");
        id = ref.id;
        revision = ref.meta.revision;
        await uploadDocument(ref.uploadUrl, this.engineSerializer());
      } else {
        const ref = await client.getDocument(id);
        revision = ref.meta.revision;
        await uploadDocument(ref.uploadUrl, this.engineSerializer());
      }
      const meta = await client.commitRevision(id, revision + 1);
      const now = Date.now();
      this.set({
        doc: {
          id,
          name: doc.name,
          localOnly: false,
          saveState: "saved",
          savedAt: now,
        },
      });
      await idbPut(localKey(id), {
        name: doc.name,
        body: this.engineSerializer(),
        history: readHistorySnapshot(this.engineHistorySerializer),
        at: now,
        cloud: true,
      });
      void meta;
    } catch (e) {
      console.error("Cloud save failed", e);
      this.set({ doc: { ...this.state.doc, saveState: "error" } });
    }
  }

  async rename(name: string): Promise<void> {
    const doc = { ...this.state.doc, name };
    this.set({ doc });
    if (doc.id && this.state.client && this.state.auth.status === "signed-in") {
      try {
        await this.state.client.updateDocumentMeta(doc.id, { name });
      } catch {
        /* rename will sync on next save */
      }
    }
  }

  async openDocument(id: string): Promise<void> {
    if (!this.state.client) return;
    const ref = await this.state.client.getDocument(id);
    const res = await fetch(ref.downloadUrl);
    if (res.ok) {
      const json = await res.text();
      this.engineLoader?.(json);
      this.set({
        doc: { ...this.state.doc, id, name: ref.meta.name, localOnly: false, saveState: "clean" },
      });
      await idbPut(localKey(id), {
        name: ref.meta.name,
        body: json,
        history: readHistorySnapshot(this.engineHistorySerializer),
        at: Date.now(),
        cloud: true,
      });
    }
  }

  async deleteDocument(id: string): Promise<void> {
    if (this.state.client) {
      try {
        await this.state.client.deleteDocument(id);
      } catch {
        /* ignore; remove local copy regardless */
      }
    }
    await idbDelete(localKey(id));
  }
}

function localKey(id: string | null): string {
  return id ? `doc:${id}` : "doc:scratch";
}

function readHistorySnapshot(serializer: (() => string) | null): string | undefined {
  if (!serializer) return undefined;
  try {
    return serializer();
  } catch {
    return undefined;
  }
}

async function uploadDocument(uploadUrl: string, body: string): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body,
  });
  if (!res.ok) throw new Error(`Upload failed (${res.status})`);
}

function readTokens(): Tokens | null {
  try {
    const raw = localStorage.getItem(TOKENS_KEY);
    return raw ? (JSON.parse(raw) as Tokens) : null;
  } catch {
    return null;
  }
}

function decodeJwt(token: string): { email?: string } | null {
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    return JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/"))) as { email?: string };
  } catch {
    return null;
  }
}

function createDevJwt(email: string): string {
  const header = btoa(JSON.stringify({ alg: "HS256", typ: "JWT" }));
  const payload = btoa(
    JSON.stringify({
      sub: "dev-user-local",
      email,
      exp: Math.floor(Date.now() / 1000) + 86400,
    }),
  );
  return `${header}.${payload}.devsignature`;
}


export const store = new Store();

export function useAppState(): AppState {
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}
